import { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "@/contexts/ThemeContext";
import { getBusinessConfig } from "@/lib/businessConfig";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { themeState } = useTheme();
  const { settings } = useSiteSettings();
  const businessType = themeState.published.businessType;
  const h = settings.header;

  const showLogo = h.showLogoInsteadOfText && !!h.siteLogo;

  const navLinks = useMemo(() => {
    const config = getBusinessConfig(businessType);
    return config.pages.map((p) => ({
      name: p.label,
      path: p.id === "home" ? "/" : `/${p.id}`,
    }));
  }, [businessType]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMobileMenuOpen]);

  const phoneHref = `tel:${settings.contact.phone.replace(/\s/g, "")}`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-card/95 backdrop-blur-md shadow-luxury py-2 md:py-3"
          : "bg-transparent py-3 md:py-6"
      }`}
    >
        <div className="container-luxury px-4 sm:px-6">
        <div className="flex items-center justify-between">
          <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-1 md:gap-2 mr-auto shrink-0">
            {showLogo ? (
              <img src={h.siteLogo} alt={`${h.brandName1} ${h.brandName2}`} className="max-h-8 md:max-h-10 object-contain" width={160} height={40} />
            ) : (
              <>
                <span className={`font-serif text-lg md:text-2xl font-bold tracking-tight transition-colors duration-300 ${isScrolled ? "text-foreground" : "text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]"}`}>
                  {h.brandName1}
                </span>
                <span className="gold-gradient-text font-serif text-lg md:text-2xl font-bold">{h.brandName2}</span>
              </>
            )}
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className={`font-sans text-xs xl:text-sm font-medium uppercase tracking-wider transition-colors duration-300 hover:text-accent ${
                  isScrolled ? "text-foreground" : "text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]"
                } ${location.pathname === link.path ? "text-accent" : ""}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3 xl:gap-4 ml-4 xl:ml-6">
            <a href={phoneHref} className={`flex items-center gap-2 font-sans text-xs xl:text-sm font-medium transition-colors duration-300 ${isScrolled ? "text-foreground" : "text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]"}`}>
              <Phone className="w-4 h-4" />
              <span className="hidden xl:inline">{settings.contact.phone}</span>
            </a>
            <Link to={h.ctaButtonLink} className="btn-gold text-[10px] xl:text-xs py-2 xl:py-3 px-4 xl:px-6">
              {h.ctaButtonText}
            </Link>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 transition-colors ${isScrolled ? "text-foreground" : "text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]"}`}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-card border-t border-border"
          >
            <nav className="container-luxury px-4 sm:px-6 py-4 flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className={`font-sans text-base font-medium py-3 px-2 rounded-lg transition-colors ${
                    location.pathname === link.path ? "text-accent bg-accent/10" : "text-foreground hover:bg-muted"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 mt-2 border-t border-border">
                <a href={phoneHref} className="flex items-center gap-2 text-foreground font-medium mb-4 py-2">
                  <Phone className="w-4 h-4" /><span>{settings.contact.phone}</span>
                </a>
                <Link to={h.ctaButtonLink} className="btn-gold w-full text-center py-3 text-sm">{h.ctaButtonText}</Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
